import React, { createContext, useContext, useReducer, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { User } from '../types/api';
import type { LoginCredentials } from '../services/authService';

// Auth types for context
export interface AuthTokens {
  access_token: string; // Legacy
  token?: string; // OpenAPI
  refresh_token: string;
  expires_at: string;
}

export interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface AuthContextType {
  // State properties for direct access
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (credentials: LoginCredentials) => Promise<User>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  clearError: () => void;
}
import {
  login as apiLogin,
  logout as apiLogout,
  getCurrentUser,
  refreshAuthToken,
  getStoredTokens,
  getStoredUser,
  clearStoredTokens,
  hasValidToken,
} from '../services/authService';
import { normalizeUserRole } from '../utils/userUtils';

// Initial auth state
const initialState: AuthState = {
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Auth action types
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; tokens: AuthTokens } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'AUTH_LOGOUT'; payload?: { preserveLoading?: boolean } }
  | { type: 'AUTH_CLEAR_ERROR' }
  | { type: 'AUTH_SET_LOADING'; payload: boolean }
  | { type: 'AUTH_UPDATE_USER'; payload: User }
  | { type: 'AUTH_UPDATE_TOKENS'; payload: AuthTokens };

// Auth reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  // Debug logging for auth state changes
  if (import.meta.env.DEV) {
    console.log('Auth Action:', action.type, action);
  }

  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case 'AUTH_SUCCESS':
      const normalizedUser = normalizeUserRole(action.payload.user);
      // Ensure user has valid role information before marking as authenticated
      const hasValidRole = normalizedUser && (normalizedUser.role || normalizedUser.role_info?.value);

      if (import.meta.env.DEV) {
        console.log('AUTH_SUCCESS: Role validation', {
          originalUser: action.payload.user,
          normalizedUser,
          hasValidRole,
          role: normalizedUser?.role,
          role_info: normalizedUser?.role_info
        });
      }

      return {
        ...state,
        user: normalizedUser,
        tokens: action.payload.tokens,
        isAuthenticated: !!hasValidRole,
        isLoading: false,
        error: hasValidRole ? null : 'User role information is missing',
      };

    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };

    case 'AUTH_LOGOUT':
      return {
        ...state,
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: action.payload?.preserveLoading ? state.isLoading : false,
        error: null,
      };

    case 'AUTH_CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    case 'AUTH_SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    case 'AUTH_UPDATE_USER':
      const updatedUser = normalizeUserRole(action.payload);
      const hasValidUpdatedRole = updatedUser && (updatedUser.role || updatedUser.role_info?.value);

      if (import.meta.env.DEV) {
        console.log('AUTH_UPDATE_USER: Role validation', {
          originalPayload: action.payload,
          updatedUser,
          hasValidUpdatedRole,
          role: updatedUser?.role,
          role_info: updatedUser?.role_info
        });
      }

      // Only update if the new user data has valid role information
      if (hasValidUpdatedRole) {
        return {
          ...state,
          user: updatedUser,
        };
      } else {
        console.warn('AUTH_UPDATE_USER: Ignoring user update without valid role information');
        return state;
      }

    case 'AUTH_UPDATE_TOKENS':
      return {
        ...state,
        tokens: action.payload,
      };

    default:
      return state;
  }
};

// Create auth context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Export context for direct usage if needed
export { AuthContext };

// Auth provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        dispatch({ type: 'AUTH_START' });

        // Check if we have valid stored tokens
        if (hasValidToken()) {
          const storedTokens = getStoredTokens();
          const storedUser = getStoredUser();

          if (storedTokens && storedUser) {
            // Ensure user role is properly normalized
            const normalizedUser = normalizeUserRole(storedUser);

            if (!normalizedUser || (!normalizedUser.role && !normalizedUser.role_info?.value)) {
              console.warn('User has no valid role information, clearing auth', {
                storedUser,
                normalizedUser,
                hasRole: !!normalizedUser?.role,
                hasRoleInfo: !!normalizedUser?.role_info?.value
              });
              clearStoredTokens();
              dispatch({ type: 'AUTH_LOGOUT', payload: { preserveLoading: true } });
              return;
            }

            // Debug log successful role normalization
            if (import.meta.env.DEV) {
              console.log('Auth initialization successful:', {
                userRole: normalizedUser.role,
                roleInfo: normalizedUser.role_info,
                email: normalizedUser.email
              });
            }

            // We have valid stored auth data
            const authTokens: AuthTokens = {
              access_token: storedTokens.token,
              token: storedTokens.token,
              refresh_token: storedTokens.refresh_token,
              expires_at: storedTokens.expires_at,
            };
            dispatch({
              type: 'AUTH_SUCCESS',
              payload: { user: normalizedUser, tokens: authTokens },
            });

            // Optionally refresh user data from server
            try {
              const currentUser = await getCurrentUser();
              const normalizedCurrentUser = normalizeUserRole(currentUser);

              if (import.meta.env.DEV) {
                console.log('Auth refresh from server:', {
                  currentUser,
                  normalizedCurrentUser,
                  hasRole: !!(normalizedCurrentUser?.role || normalizedCurrentUser?.role_info?.value)
                });
              }

              if (normalizedCurrentUser && (normalizedCurrentUser.role || normalizedCurrentUser.role_info?.value)) {
                dispatch({ type: 'AUTH_UPDATE_USER', payload: normalizedCurrentUser });
              } else {
                console.warn('Server returned user without valid role information, keeping stored user data');
              }
            } catch (error) {
              // Failed to refresh user data, continue with stored user data
              if (import.meta.env.DEV) {
                console.log('Failed to refresh user data from server:', error);
              }
            }
          } else {
            // Invalid stored data, clear and set as unauthenticated
            clearStoredTokens();
            dispatch({ type: 'AUTH_LOGOUT', payload: { preserveLoading: true } });
          }
        } else {
          // No valid tokens, set as unauthenticated
          dispatch({ type: 'AUTH_LOGOUT', payload: { preserveLoading: true } });
        }
      } catch (error) {
        // Auth initialization error
        clearStoredTokens();
        dispatch({ type: 'AUTH_FAILURE', payload: 'Authentication initialization failed' });
      } finally {
        dispatch({ type: 'AUTH_SET_LOADING', payload: false });
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (credentials: LoginCredentials): Promise<User> => {
    try {
      dispatch({ type: 'AUTH_START' });

      const authResponse = await apiLogin(credentials); // Returns AuthResponse directly
      const rawUser = authResponse.user;
      const normalizedUser = normalizeUserRole(rawUser);

      if (!normalizedUser || (!normalizedUser.role && !normalizedUser.role_info?.value)) {
        throw new Error('User has no valid role information');
      }

      const tokens: AuthTokens = {
        access_token: authResponse.token || '',
        token: authResponse.token,
        refresh_token: authResponse.refresh_token,
        expires_at: authResponse.expires_at,
      };

      // Login successful
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user: normalizedUser, tokens },
      });

      return normalizedUser; // Return normalized user for role-based redirects
    } catch (error: any) {
      const errorMessage = error.message || 'Login failed';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      throw error; // Re-throw so components can handle it
    }
  };

  // Logout function
  const logout = async (): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_SET_LOADING', payload: true });
      await apiLogout();
    } catch (error) {
      // Logout error
      // Continue with logout even if API call fails
    } finally {
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };

  // Refresh token function
  const refreshToken = async (): Promise<void> => {
    try {
      const currentTokens = getStoredTokens();
      if (!currentTokens?.refresh_token) {
        throw new Error('No refresh token available');
      }

      const authResponse = await refreshAuthToken(currentTokens.refresh_token);
      const newTokens: AuthTokens = {
        access_token: authResponse.token,
        token: authResponse.token,
        refresh_token: authResponse.refresh_token,
        expires_at: authResponse.expires_at,
      };
      dispatch({ type: 'AUTH_UPDATE_TOKENS', payload: newTokens });
    } catch (error: any) {
      // Token refresh error
      // If refresh fails, logout user
      await logout();
      throw error;
    }
  };

  // Clear error function
  const clearError = (): void => {
    dispatch({ type: 'AUTH_CLEAR_ERROR' });
  };

  // Context value
  const contextValue: AuthContextType = {
    ...state,
    login,
    logout,
    refreshToken,
    clearError,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// HOC for components that require authentication
export const withAuth = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  return (props: P) => {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}>
          Loading...
        </div>
      );
    }

    if (!isAuthenticated) {
      // Redirect to login or show login form
      window.location.href = '/login';
      return null;
    }

    return <Component {...props} />;
  };
};
